import { z } from "zod";

/**
 * Staff roles and the deny-by-default permission matrix (docs/08 section 3, docs/06 section 7; ADR-0035).
 *
 * - Roles live in `role_assignments` (granted and revoked only by the audited operator CLI, with a reason), never on the
 *   profile and never in a request. The server reads them on every staff request.
 * - Every staff route checks ONE permission through the API's `requirePermission` (apps/api/src/services/permissions.ts);
 *   the repositories re-check the same permission inside their transactions where they did before.
 * - Deny by default: a role that is not in the matrix, a permission that is not in the matrix, an empty role list and a
 *   scoped (non-"*") assignment all grant nothing. `admin` holds every permission.
 * - The web app reads this module only to hide links and sections; hiding is cosmetic, the server is the authority.
 */

/** Grantable staff roles (operator CLI `grant-role --role ...`). `support` is deliberately absent (ADR-0035). */
export const STAFF_ROLES = ["staff", "moderator", "finance", "admin"] as const;
export const StaffRole = z.enum(STAFF_ROLES);
export type StaffRole = z.infer<typeof StaffRole>;

/** Every staff permission. Names are `<area>.<action>`; the order here is the order shown to staff. */
export const STAFF_PERMISSIONS = [
  // Review queues (ADR-0009 #5, ADR-0011, ADR-0013)
  "creatives.review",
  "campaigns.review",
  "reports.read",
  "reports.decide",
  "review.own_submissions",
  // Townsy-owned content (ADR-0011 #1, ADR-0004)
  "house_campaigns.manage",
  "house_stores.edit",
  // Operations (ADR-0035)
  "audit.read",
  "roles.read",
  // Finance staff tools (ADR-0040)
  "finance.cases.read",
  "finance.cases.resolve",
  "finance.refunds.retry",
  "finance.alerts.ack",
  "finance.ledger.read",
  // Later M7 slices: no route uses these yet
  "users.read",
  "users.moderate",
  "flags.read",
  "flags.manage",
  "world.manage",
  "privacy.requests.manage",
] as const;
export const StaffPermission = z.enum(STAFF_PERMISSIONS);
export type StaffPermission = z.infer<typeof StaffPermission>;

/**
 * How much assurance a permission needs beyond an active session (docs/08 section 3: "Admin/finance access requires MFA
 * for launch and step-up confirmation for dangerous operations"; ADR-0039), enforced inside the API's
 * `requirePermission`:
 * - `standard`: the account has two-step sign-in (TOTP) turned on and THIS session proved it (MFA_REQUIRED /
 *   STEP_UP_REQUIRED otherwise);
 * - `dangerous`: also a confirmation with a current authenticator code on this session within STAFF_STEP_UP_MINUTES
 *   (STEP_UP_REQUIRED otherwise).
 */
export type PermissionLevel = "standard" | "dangerous";

export interface PermissionInfo {
  level: PermissionLevel;
  /** Start of the refusal: "<subject> needs the moderator or admin role." */
  subject: string;
  /** The subject is plural ("Reports need ..."). */
  plural?: true;
  /** Completes "Sign in to ..." for a visitor without an account. */
  signIn: string;
  /** One line for staff (the admin hub's role table and the ADR). */
  description: string;
}

export const PERMISSION_INFO: Readonly<Record<StaffPermission, PermissionInfo>> = {
  "creatives.review": {
    level: "standard",
    subject: "Creative review",
    signIn: "review creatives",
    description: "Review advertiser designs (approve, reject, revoke, close urgent flags) and preview their uploads.",
  },
  "campaigns.review": {
    level: "standard",
    subject: "Campaign review",
    signIn: "review campaigns",
    description: "Review sponsored-task campaigns (approve, reject, suspend) and read their reports.",
  },
  "reports.read": { level: "standard", subject: "Reports", plural: true, signIn: "review reports", description: "Read reports about people with their protected snapshots." },
  "reports.decide": {
    level: "standard",
    subject: "Reports",
    plural: true,
    signIn: "review reports",
    description: "Decide reports about people: dismiss, resolve, mute for 24 hours or ban (note required).",
  },
  "review.own_submissions": {
    level: "standard",
    subject: "Reviewing your own submissions",
    signIn: "review your own submissions",
    description: "Decide your own designs and campaigns, only where ADMIN_SELF_REVIEW_ALLOWED is on (ADR-0012).",
  },
  "house_campaigns.manage": {
    level: "standard",
    subject: "House campaigns",
    plural: true,
    signIn: "manage house campaigns",
    description: "Create, fund (house allocation, not revenue) and run Townsy house campaigns.",
  },
  "house_stores.edit": { level: "standard", subject: "Editing Townsy demo stores", signIn: "edit Townsy demo stores", description: "Edit the store board of Townsy-owned demo stores (audited)." },
  "audit.read": { level: "standard", subject: "The audit log", signIn: "read the audit log", description: "Read the admin audit log (redacted entries exactly as stored)." },
  "roles.read": { level: "standard", subject: "The staff role list", signIn: "see staff roles", description: "See who holds which staff role, who granted it and why." },
  "finance.cases.read": {
    level: "standard",
    subject: "Financial cases",
    plural: true,
    signIn: "see financial cases",
    description: "Read dispute, refund and support cases, compensation refunds and finance alerts.",
  },
  "finance.cases.resolve": { level: "dangerous", subject: "Resolving financial cases", signIn: "resolve financial cases", description: "Resolve a financial case with the approved plan (reason required, audited)." },
  "finance.refunds.retry": { level: "dangerous", subject: "Retrying refunds", signIn: "retry refunds", description: "Retry a failed original-method compensation refund (reason required, audited)." },
  "finance.alerts.ack": { level: "standard", subject: "Finance alerts", plural: true, signIn: "acknowledge finance alerts", description: "Acknowledge operator finance alerts with a note (audited)." },
  "finance.ledger.read": {
    level: "standard",
    subject: "The ledger",
    signIn: "read the ledger",
    description: "Read orders, a resident's coin and credit journals and credit provenance, the finance report and the integrity check.",
  },
  "users.read": { level: "standard", subject: "Account lookup", signIn: "look up accounts", description: "Look up an account's status and restrictions (later M7 slice)." },
  "users.moderate": { level: "dangerous", subject: "Moderating accounts", signIn: "moderate accounts", description: "Restrict or lift restrictions on an account directly (later M7 slice)." },
  "flags.read": { level: "standard", subject: "Feature flags", plural: true, signIn: "see feature flags", description: "Read feature switches (later M7 slice)." },
  "flags.manage": { level: "dangerous", subject: "Changing feature flags", signIn: "change feature flags", description: "Change feature switches with a reason (later M7 slice)." },
  "world.manage": { level: "dangerous", subject: "World management", signIn: "manage the world", description: "Change properties, catalogs and billboards (later M7 slice)." },
  "privacy.requests.manage": { level: "dangerous", subject: "Privacy requests", plural: true, signIn: "handle privacy requests", description: "See data export and account deletion requests, their dates and why a deletion waits (read-only)." },
};

/**
 * The matrix: which permissions each role holds. `admin` holds every permission (derived from the list, so a new
 * permission can never be forgotten for it). Everything not listed is denied.
 */
export const ROLE_PERMISSIONS: Readonly<Record<StaffRole, readonly StaffPermission[]>> = {
  staff: ["house_campaigns.manage", "house_stores.edit"],
  moderator: ["creatives.review", "campaigns.review", "reports.read", "reports.decide", "users.moderate"],
  finance: ["finance.cases.read", "finance.cases.resolve", "finance.refunds.retry", "finance.alerts.ack", "finance.ledger.read"],
  admin: STAFF_PERMISSIONS,
};

const ROLE_SET: ReadonlySet<string> = new Set(STAFF_ROLES);
const PERMISSION_SET: ReadonlySet<string> = new Set(STAFF_PERMISSIONS);

export const isStaffRole = (role: string): role is StaffRole => ROLE_SET.has(role);
export const isStaffPermission = (permission: string): permission is StaffPermission => PERMISSION_SET.has(permission);

/** The staff roles among `roles` (unknown names dropped), in matrix order. */
export function staffRolesOf(roles: readonly string[] | null | undefined): StaffRole[] {
  const held = new Set(roles ?? []);
  return STAFF_ROLES.filter((r) => held.has(r));
}

/** Every permission the roles grant, in matrix order. Unknown roles grant nothing; names are case-sensitive. */
export function permissionsOf(roles: readonly string[] | null | undefined): StaffPermission[] {
  const granted = new Set<StaffPermission>();
  for (const role of staffRolesOf(roles)) for (const p of ROLE_PERMISSIONS[role]) granted.add(p);
  return STAFF_PERMISSIONS.filter((p) => granted.has(p));
}

/** Deny by default: true only when a known role in `roles` grants this known permission. */
export function hasPermission(roles: readonly string[] | null | undefined, permission: string): boolean {
  if (!isStaffPermission(permission)) return false;
  return staffRolesOf(roles).some((r) => ROLE_PERMISSIONS[r].includes(permission));
}

/** True when the roles grant at least one staff permission (the admin hub and its menu link). */
export const hasAnyStaffPermission = (roles: readonly string[] | null | undefined): boolean => permissionsOf(roles).length > 0;

/** The roles that hold a permission, in matrix order. */
export function rolesWithPermission(permission: StaffPermission): StaffRole[] {
  return STAFF_ROLES.filter((r) => ROLE_PERMISSIONS[r].includes(permission));
}

/** "Creative review needs the moderator or admin role." — generated from the matrix, so it never goes stale. */
export function permissionDeniedMessage(permission: StaffPermission): string {
  const info = PERMISSION_INFO[permission];
  const roles = rolesWithPermission(permission);
  const list = roles.length > 1 ? `${roles.slice(0, -1).join(", ")} or ${roles[roles.length - 1]}` : (roles[0] ?? "admin");
  return `${info.subject} ${info.plural ? "need" : "needs"} the ${list} role.`;
}
